import { Component, ElementRef, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { UserConfigLotService } from "./user-config-lot.service";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { AuthService } from "../../shared/services/auth.service";
import { ToastrService } from "ngx-toastr";
import { EditPermissionModalComponent } from './edit-permission/edit-permission.modal.component';

@Component({
  selector: 'app-user-config-lot',
  templateUrl: './user-config-lot.component.html',
  styleUrls: ['./user-config-lot.component.scss']
})
export class UserConfigLotComponent {
  public isInsert: boolean;
  public isSubmitted: boolean;
  public isLoaded: boolean;
  public users: any[] = [];
  public selectedUser: any = null;
  public validationCode: string = '';
  public validatingTotp: boolean = false;
  public errorMessage: string = '';
  public pendingAction: 'create' | 'toggle' | 'reset' | null = null;
  public pendingUser: any = null;
  public resettingId: string | null = null;
  public pendingResetUser: any = null;
  public readonly allowedByRole = this.authService.isAccountAdm();

  @ViewChild('requestTOTPCode') validateToken: ElementRef | any;

  user = new FormGroup({
    email: new FormControl<string>('', [Validators.required, Validators.email]),
    name: new FormControl<string>('', Validators.required),
    rolesAccount: new FormControl<number>(3, Validators.required),
    terms: new FormControl<boolean>(false),
    permissionsLottery: new FormGroup({
      activityLog: new FormControl<boolean>(false, { nonNullable: true }),
      betSummary: new FormControl<boolean>(false, { nonNullable: true }),
      financeIndicators: new FormControl<boolean>(false, { nonNullable: true }),
      financialSummary: new FormControl<boolean>(false, { nonNullable: true }),
      monitoring: new FormControl<boolean>(false, { nonNullable: true }),
      operationIndicators: new FormControl<boolean>(false, { nonNullable: true }),
      operatorList: new FormControl<boolean>(false, { nonNullable: true }),
      operators: new FormControl<boolean>(false, { nonNullable: true }),
      overview: new FormControl<boolean>(false, { nonNullable: true }),
      playerList: new FormControl<boolean>(false, { nonNullable: true }),
      product: new FormControl<boolean>(false, { nonNullable: true }),
      transactionalAccounts: new FormControl<boolean>(false, { nonNullable: true }),
      userConfigLot: new FormControl<boolean>(false, { nonNullable: true }),
      usersIndicators: new FormControl<boolean>(false, { nonNullable: true }),
    }),
  });

  rolesOptions = [
    { value: 1, name: 'common.standard' },
    { value: 3, name: 'common.readOnly' },
  ];

  permissionLotteryOptions = [
    { key: 'overview', label: 'Visão Geral' },
    { key: 'transactionalAccounts', label: 'Contas Transacionais' },
    { key: 'operationIndicators', label: 'Indicadores de Operação' },
    { key: 'product', label: 'Produtos' },
    { key: 'operators', label: 'Operadores' },
    { key: 'usersIndicators', label: 'Indicadores de Usuários' },
    { key: 'playerList', label: 'Lista de Jogadores' },
    { key: 'monitoring', label: 'Monitoramento' },
    { key: 'financeIndicators', label: 'Indicadores de Finanças' },
    { key: 'operatorList', label: 'Lista de Operadores' },
    { key: 'financialSummary', label: 'Resumo Financeiro' },
    { key: 'betSummary', label: 'Resumo de Apostas' },
    { key: 'userConfigLot', label: 'Usuários Loteria' },
    { key: 'activityLog', label: 'Log de Usuários' },
  ];

  permissionColumns = this.chunkPermissions(this.permissionLotteryOptions, 4);

  constructor(
    private userConfigLotService: UserConfigLotService,
    private modalService: NgbModal,
    private authService: AuthService,
    private toastr: ToastrService
  ) {
    this.isInsert = false;
    this.isSubmitted = false;
    this.isLoaded = false;
    this.loadUsers();
  }

  private chunkPermissions(list: any[], size: number): any[][] {
    const chunks: any[][] = [];
    for (let i = 0; i < list.length; i += size) {
      chunks.push(list.slice(i, i + size));
    }
    return chunks;
  }

  private getPermissionsDefault() {
    return {
      reports: false,
      subAccounts: false,
      extrato: false,
      resumo: false,
      infracoes: false,
      comissoes: false,
      carteiraComissoes: false,
      deposito: false,
      depositoPix: false,
      transferencia: false,
      saqueCopiaCola: false,
      saqueBoleto: false,
      transferBalance: false,
      approveWithdraw: false,
      reservaFinanceira: false,
      criptomoeda: false,
      wallet: false,
      accountView: false,
      webhook: false,
      userConfig: false,
      bankingCredentials: false,
      technicalEvents: false,
      apiCredentials: false,
    };
  }

  private getPermissionsLotteryDefault() {
    return {
      activityLog: false,
      betSummary: false,
      financeIndicators: false,
      financialSummary: false,
      monitoring: false,
      operationIndicators: false,
      operatorList: false,
      operators: false,
      overview: false,
      playerList: false,
      product: false,
      transactionalAccounts: false,
      userConfigLot: false,
      usersIndicators: false,
    };
  }

  private resetInsertForm() {
    this.user.reset({
      email: '',
      name: '',
      rolesAccount: 3,
      terms: false,
    });
    this.user.controls.permissionsLottery.reset(this.getPermissionsLotteryDefault());
    this.isSubmitted = false;
  }

  loadUsers() {
    this.isLoaded = false;
    this.userConfigLotService.getUsers().subscribe(response => {
      this.users = Array.isArray(response.users) ? response.users : [];
      this.isLoaded = true;
    });
  }

  async pasteTotpCode() {
    try {
      const text = await navigator.clipboard.readText();
      this.validationCode = text.trim();
    } catch {
      this.toastr.error('Erro ao colar o código do clipboard');
    }
  }

  doCreateUser() {
    const { email, name } = this.user.value;
    const rolesAccount = Number(this.user.value.rolesAccount);

    const userData = {
      email,
      name,
      rolesAccount,
      admCreate: false,
      permissions: this.getPermissionsDefault(),
      permissionsLottery: this.user.controls.permissionsLottery.getRawValue()
    };

    this.userConfigLotService.addUser(userData).subscribe({
      next: () => {
        this.toastr.success('Usuário criado com sucesso!', 'Sucesso');
        this.resetInsertForm();
        this.return();
        this.isLoaded = true;
      },
      error: (error) => {
        const mensagem = error?.error?.mensagem || 'Erro ao criar usuário.';
        this.toastr.error(mensagem, 'Erro');
        this.isLoaded = true;
      }
    });
  }

  onEditClick(user: any): void {
    if (!this.allowedByRole) {
      this.toastr.error('Seu perfil não pode editar permissões.', 'Acesso negado');
      return;
    }

    const modalRef = this.modalService.open(EditPermissionModalComponent, {
      size: 'xl',
      centered: true,
      backdrop: 'static',
      keyboard: true,
      windowClass: 'modal-wide',
    });

    modalRef.componentInstance.user = user;

    modalRef.closed.subscribe(() => {
      this.loadUsers();
    });
  }

  onResetClick(user: any) {
    if (!this.authService.isCommon()) {
      this.toastr.error('Ação não permitida.');
      return;
    }

    this.pendingResetUser = user;
    this.pendingAction = 'reset';
    this.validationCode = '';
    this.errorMessage = '';

    this.modalService.open(this.validateToken, {
      backdrop: 'static',
      keyboard: true,
      centered: true,
      size: 'sm'
    });
  }

  async validateTotp() {
    if (this.validatingTotp) return;
    this.validatingTotp = true;

    this.isLoaded = false;
    const isValid = await this.authService.validateTotpCode(this.validationCode).then(r => r.isValid);

    if (isValid) {
      this.modalService.dismissAll();
      this.validationCode = '';
      this.errorMessage = '';

      if (this.pendingAction === 'create') {
        this.doCreateUser();
      } else if (this.pendingAction === 'toggle') {
        this.doToggleUserActive(this.pendingUser);
      } else if (this.pendingAction === 'reset' && this.pendingResetUser) {
        await this.doResetPassword(this.pendingResetUser);
      }

      this.pendingAction = null;
      this.pendingUser = null;
      this.pendingResetUser = null;
    } else {
      this.errorMessage = 'Código de autenticação inválido!';
      this.isLoaded = false;
    }
    this.validatingTotp = false;
  }

  private openTotpModal() {
    this.modalService.open(this.validateToken, {
      backdrop: 'static',
      keyboard: true,
      centered: true,
      size: 'sm',
    });
  }

  onToggleClick(ev: MouseEvent, modalTpl: any, user: any) {
    ev.preventDefault();
    ev.stopPropagation();

    if (!this.allowedByRole) {
      this.toastr.error('Seu perfil não pode ativar/desativar usuários.', 'Acesso negado');
      return;
    }

    this.selectedUser = user;
    this.modalService.open(modalTpl, { size: 'sm' });
  }

  toggleActive(user: any) {
    if (!this.allowedByRole) {
      this.toastr.error('Seu perfil não pode ativar/desativar usuários.', 'Acesso negado');
      return;
    }
    this.modalService.dismissAll();
    this.pendingAction = 'toggle';
    this.pendingUser = user;
    this.openTotpModal();
  }

  doToggleUserActive(user: any) {
    this.userConfigLotService.toggleUserActive(user.email, !user.active).subscribe({
      next: () => {
        this.toastr.success('Status do usuário atualizado com sucesso!', 'Sucesso');
        this.loadUsers();
        this.isLoaded = true;
      },
      error: (err) => {
        this.toastr.error('Erro ao atualizar o status do usuário.', 'Erro');
        console.error(err);
        this.loadUsers();
        this.isLoaded = true;
      }
    });
  }

  private async doResetPassword(user: any) {
    this.resettingId = user.id;

    this.userConfigLotService.resetPassword(user.email).subscribe({
      next: () => {
        this.toastr.success(`Senha de ${user.name} resetada com sucesso!`, 'Sucesso');
        this.loadUsers();
      },
      error: (err) => {
        const msg = err?.error?.mensagem || 'Falha ao resetar a senha do usuário.';
        this.toastr.error(msg, 'Erro');
        console.error(err);
      },
      complete: () => {
        this.resettingId = null;
      }
    });
  }

  showConfirmEnableDisable(modal: any, user: any) {
    if (!this.allowedByRole) {
      this.toastr.error('Seu perfil não pode ativar/desativar usuários.', 'Acesso negado');
      return;
    }
    this.selectedUser = user;
    this.modalService.open(modal, { size: 'sm' });
  }

  openInsert() {
    if (!this.allowedByRole) {
      this.toastr.error('Seu perfil não pode criar usuários.', 'Acesso negado');
      return;
    }
    this.resetInsertForm();
    this.isInsert = true;
  }

  return() {
    this.isInsert = false;
    this.loadUsers();
    this.resetInsertForm();
  }

  clearForm() {
    this.resetInsertForm();
  }

  add() {
    if (!this.allowedByRole) {
      this.toastr.error('Seu perfil não pode criar usuários.', 'Acesso negado');
      return;
    }
    this.isSubmitted = true;
    if (!this.user.controls.terms.value) {
      this.toastr.error("Aceite os termos!", 'Erro');
      return;
    }
    if (this.user.valid) {
      this.pendingAction = 'create';
      this.openTotpModal();
    }
  }
}